import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { CrewTemplateLogo } from '@/components/crews/crew-template-logo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getErrorMessage } from '@/lib/api'
import crewsApi from '@/services/crews'
import type { Crew } from '@/types'

type StaffedCrewsPanelProps = {
  serviceInstanceId?: string | null
  focusTemplateId?: string | null
  onFocusHandled?: () => void
}

export function StaffedCrewsPanel({
  serviceInstanceId,
  focusTemplateId,
  onFocusHandled,
}: StaffedCrewsPanelProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [templateFilterId, setTemplateFilterId] = useState<string | null>(null)
  const focusHandledRef = useRef(false)

  const templatesQuery = useQuery({
    queryKey: ['crew-templates', serviceInstanceId],
    queryFn: () => crewsApi.listTemplates({ service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
  })

  const crewsQuery = useQuery({
    queryKey: ['crews', serviceInstanceId],
    queryFn: () => crewsApi.listCrews({ service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => crewsApi.deleteCrew(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crews'] })
      toast.success('Team deleted')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const staffUrl = (params: Record<string, string | undefined>) => {
    const p = new URLSearchParams()
    if (serviceInstanceId) p.set('service_instance_id', serviceInstanceId)
    for (const [k, v] of Object.entries(params)) {
      if (v) p.set(k, v)
    }
    return `/services/crews/staff?${p.toString()}`
  }

  const templateById = useMemo(
    () => new Map((templatesQuery.data ?? []).map((t) => [String(t.id), t])),
    [templatesQuery.data],
  )

  const visibleCrews = useMemo(() => {
    const crews = crewsQuery.data ?? []
    return templateFilterId
      ? crews.filter((c) => String(c.crew_template) === templateFilterId)
      : crews
  }, [crewsQuery.data, templateFilterId])

  const filteredTemplateName = templateFilterId ? templateById.get(templateFilterId)?.name : null

  useEffect(() => {
    focusHandledRef.current = false
  }, [focusTemplateId])

  useEffect(() => {
    if (focusHandledRef.current || !focusTemplateId) return
    if (templatesQuery.isLoading || crewsQuery.isLoading) return
    setTemplateFilterId(focusTemplateId)
    focusHandledRef.current = true
    onFocusHandled?.()
  }, [crewsQuery.isLoading, focusTemplateId, onFocusHandled, templatesQuery.isLoading])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Staffed teams</h2>
          <p className="text-sm text-muted-foreground">Assign workers to role slots to build ready-to-deploy teams.</p>
        </div>
        <Button onClick={() => navigate(staffUrl({}))} disabled={!serviceInstanceId}>
          <Plus className="mr-2 h-4 w-4" />
          New team
        </Button>
      </div>

      {filteredTemplateName ? (
        <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/20 px-3 py-2 text-sm">
          <span className="text-muted-foreground">Showing teams for</span>
          <span className="font-medium">{filteredTemplateName}</span>
          <Button type="button" size="sm" variant="ghost" className="ml-auto h-7 px-2" onClick={() => setTemplateFilterId(null)}>
            Show all
          </Button>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visibleCrews.map((crew: Crew) => {
          const template = templateById.get(String(crew.crew_template))
          return (
            <Card key={String(crew.id)}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-3">
                    <CrewTemplateLogo
                      logoUrl={crew.crew_template_logo_url ?? template?.logo_url}
                      name={crew.crew_template_name ?? crew.name}
                    />
                    <div className="min-w-0">
                      <CardTitle className="text-base">{crew.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{crew.crew_template_name}</p>
                    </div>
                  </div>
                  <Badge variant={crew.is_ready ? 'default' : 'outline'}>
                    {crew.is_ready ? 'Ready' : 'Incomplete'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1 text-xs text-muted-foreground">
                  {(crew.members ?? []).map((member) => (
                    <p key={String(member.id)}>
                      {member.slot_label || member.skill_role_name}: {member.worker_display_name}
                    </p>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(staffUrl({ crew_id: String(crew.id) }))}
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteMutation.mutate(crew.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

